/* Yalnız asistan nonce edinimi: süre kontrolü, tekil GET, ortak bekleme ve sınırlı yenileme. */
const REGISTRY = Symbol.for('onepage.storefrontAssistant.nonceManagers');
const SAFETY_MS = 60_000;

/** İstemcide yalnız biçim ve tazelik kontrol eder; HMAC yetkisi sunucuda kalır. */
export function nonceExpiry(token, now = Date.now()) {
  if (typeof token !== 'string' || token.length > 4096 || !/^[A-Za-z0-9+/]+={0,2}\.[a-f0-9]{64}$/.test(token)) return 0;
  try {
    const payload = JSON.parse(atob(token.split('.')[0]));
    const expiry = Number.isSafeInteger(payload?.exp) ? payload.exp * 1000 : 0;
    return Number.isSafeInteger(expiry) && expiry > now + SAFETY_MS ? expiry : 0;
  } catch { return 0; }
}

/** Endpoint ve sayfa kaynağını yetkisiz başka bir hedefe nonce taşımadan çözer. */
export function nonceScope(config, host = globalThis.window) {
  if (!config?.abuseNonceEndpoint || !host?.location?.href) return null;
  try {
    const page = new URL(host.location.href);
    const endpoint = new URL(config.abuseNonceEndpoint, page);
    const chat = new URL(config.endpoint || endpoint.href, page);
    if (![page, endpoint, chat].every(url => ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password)
      || endpoint.origin !== chat.origin) throw new Error('AssistantNonceScope');
    endpoint.hash = '';
    endpoint.searchParams.delete('_onepage_nonce');
    endpoint.searchParams.set('pageUrl', page.origin);
    return {endpoint: endpoint.href, origin: page.origin, key: `${endpoint.href}|${page.origin}`};
  } catch { throw new Error('AssistantNonceScope'); }
}

/** İptal edilen bekleyici diğer tüketicilerin ortak GET isteğini iptal etmez. */
function waitFor(promise, signal) {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(signal.reason || new DOMException('Aborted', 'AbortError'));
  return new Promise((resolve, reject) => {
    const abort = () => reject(signal.reason || new DOMException('Aborted', 'AbortError'));
    signal.addEventListener('abort', abort, {once: true});
    promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
  });
}

/** Sahte saat/ağ bağımlılıkları testte verilebilir; üretimde yalnız anonim nonce GET'i kullanılır. */
export function createNonceManager({scope, seed, publish, isActive = () => true, fetchImpl = (...args) => globalThis.fetch(...args),
  now = () => Date.now(), monotonic = () => globalThis.performance?.now?.() ?? Date.now(),
  timerHost = globalThis, unique = () => globalThis.crypto.randomUUID?.()
    || Array.from(globalThis.crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join(''), deadlineMs = 5000, cooldownMs = 10_000}) {
  let token = '', expiry = 0, deadline = 0, flight = null, timer = null, disposed = false, retryAt = 0;
  const usable = () => token !== '' && expiry > now() + SAFETY_MS && monotonic() < deadline;
  const remember = (value) => {
    const until = nonceExpiry(value, now());
    if (!until) throw new Error('AssistantNonceStale');
    token = value; expiry = until; deadline = monotonic() + until - now() - SAFETY_MS;
    publish?.(token);
  };
  if (nonceExpiry(seed, now())) remember(seed);
  const schedule = () => {
    timerHost.clearTimeout(timer);
    if (!disposed && isActive() && usable()) {
      timer = timerHost.setTimeout(() => { timer = null; ensure().catch(() => {}); }, Math.max(1, Math.min(2_147_483_647, expiry - now() - SAFETY_MS, deadline - monotonic())));
      timer?.unref?.();
    }
  };
  const ensure = ({signal} = {}) => {
    if (signal?.aborted) return Promise.reject(signal.reason || new DOMException('Aborted', 'AbortError'));
    if (disposed || !isActive()) return Promise.reject(new DOMException('Aborted', 'AbortError'));
    if (usable()) { if (timer === null) schedule(); return waitFor(Promise.resolve(token), signal); }
    if (!flight) {
      if (monotonic() < retryAt) return Promise.reject(new Error('AssistantNonceUnavailable'));
      const marker = {controller: new AbortController(), promise: null};
      flight = marker;
      let timeout;
      const aborted = new Promise((_, reject) => marker.controller.signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')), {once: true}));
      const timedOut = new Promise((_, reject) => {
        timeout = timerHost.setTimeout(() => { marker.controller.abort(); reject(new Error('AssistantNonceTimeout')); }, deadlineMs);
        timeout?.unref?.();
      });
      const acquire = async () => {
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            const url = new URL(scope.endpoint);
            url.searchParams.set('_onepage_nonce', unique());
            const response = await fetchImpl(url.href, {credentials: 'omit', headers: {Accept: 'application/json'}, cache: 'no-store', signal: marker.controller.signal});
            if (!response.ok) {
              if (response.status >= 400 && response.status < 500) throw Object.assign(new Error('AssistantNonceUnavailable'), {terminal: true});
              throw new Error('AssistantNonceUnavailable');
            }
            const data = await response.json();
            if (disposed || !isActive() || marker.controller.signal.aborted || flight !== marker) throw new DOMException('Aborted', 'AbortError');
            remember(data?.abuse_nonce); schedule(); return token;
          } catch (error) {
            if (disposed || !isActive() || marker.controller.signal.aborted || error.terminal || attempt === 1) throw error;
          }
        }
      };
      marker.promise = Promise.race([acquire(), timedOut, aborted]).catch(error => { retryAt = monotonic() + cooldownMs; throw error; }).finally(() => {
        timerHost.clearTimeout(timeout);
        if (flight === marker) flight = null;
      });
    }
    return waitFor(flight.promise, signal);
  };
  return {ensure, dispose() { disposed = true; timerHost.clearTimeout(timer); flight?.controller.abort(); }};
}

/** Ayrı loader/runtime bundle'ları aynı endpoint ve sayfa kaynağında tek yönetici kullanır. */
export function assistantNonceManager(config, host = globalThis.window) {
  const scope = nonceScope(config, host);
  if (!scope) return null;
  const registry = host[REGISTRY] ||= new Map();
  if (!registry.has(scope.key)) {
    for (const [key, old] of registry) { old.dispose(); registry.delete(key); }
    const isActive = () => {
      const active = host.storefrontAssistantConfig;
      try { return !active || nonceScope(active, host)?.key === scope.key; } catch { return false; }
    };
    const manager = createNonceManager({scope, seed: config.abuseNonce, isActive, publish: token => {
      config.abuseNonce = token;
      const active = host.storefrontAssistantConfig;
      try { if (active && nonceScope(active, host)?.key === scope.key) active.abuseNonce = token; } catch { /* Değişen hedef eski yenilemeyi devralmaz. */ }
    }});
    registry.set(scope.key, manager);
    host.addEventListener?.('pagehide', () => { manager.dispose(); registry.delete(scope.key); }, {once: true});
  }
  return registry.get(scope.key);
}
